import { useParams } from "@/components/hooks/use-params";
import { useRoute } from "@/components/shared/router";
import type { ContactFormData } from "@/store/auth/forms/useContactForm";
import { useContactForm } from "@/store/auth/forms/useContactForm";
import { useUpdateContactMutation } from "@/gqlhooks/profile/useUpdateContactMutation";
import {
  getErrorMessage,
  ensurePhoneNumberWithPlus,
} from "@/lib/helpers/string";
import { useEffect } from "react";
import { showToast } from "@/components/ui/toast/show-toast";
import { FormProvider } from "react-hook-form";
import { UpdateContactScreen } from "./update-contact-screen";
import { useClientContactsQuery } from "@/gqlhooks/profile/useClientContactsQuery";
import type { UpdateContactProps } from "@/types/profile";

export { type UpdateContactProps };

export const UpdateContact = () => {
  const { id } = useParams<{ id: string }>();
  const { push, pull } = useRoute();

  const { data: contacts, loading: contactsLoading } = useClientContactsQuery();
  const contact = contacts?.find((contact) => contact.id === id);
  const isEdit = !!contact?.primary;

  useEffect(() => {
    if (!contactsLoading && !contact) {
      push({
        pageKey: "contacts",
      });
    }
  }, [contact, contactsLoading]);

  const currentValues: ContactFormData = {
    firstName: contact?.firstName ?? "",
    lastName: contact?.lastName ?? "",
    primary: contact?.primary ?? false,
    email: contact?.email.map((e) => ({ value: e })) ?? [{ value: "" }],
    phoneNumber: contact?.phoneNumber.map((p) => ({
      value: ensurePhoneNumberWithPlus(p),
    })) ?? [{ value: "+65" }],
  };

  const contactFormMethods = useContactForm(currentValues);
  const { handleSubmit } = contactFormMethods;

  const [updateContactMutation, { loading: updateContactLoading }] =
    useUpdateContactMutation();

  const onSubmit = handleSubmit(async (data: ContactFormData) => {
    try {
      if (!id) return;

      const formattedPhoneNumbers = data.phoneNumber?.map((phone) => {
        return ensurePhoneNumberWithPlus(phone.value);
      });

      const formattedEmails = data.email?.map((email) => email.value);

      const result = await updateContactMutation({
        variables: {
          input: {
            id: id,
            firstName: data.firstName,
            lastName: data.lastName,
            email: formattedEmails,
            phoneNumber: formattedPhoneNumbers,
            primary: data.primary ?? false,
          },
        },
      });

      if (result.data?.updateContact?.contact) {
        if (result.data.updateContact.contact.primary) {
          const existingContacts = contacts || [];
          const lastPrimaryContact = existingContacts.filter(
            (cont) => cont.primary && cont.id !== id, // Exclude current contact
          );
          if (lastPrimaryContact.length > 0) {
            try {
              await Promise.all(
                lastPrimaryContact.map((contact) =>
                  updateContactMutation({
                    variables: {
                      input: {
                        id: contact.id,
                        firstName: contact.firstName,
                        lastName: contact.lastName,
                        email: contact.email,
                        phoneNumber: contact.phoneNumber,
                        primary: false,
                      },
                    },
                  }),
                ),
              );
              pull();
              showToast({
                type: "success",
                title: "Success update contact",
              });
              return;
            } catch (error) {
              const errMsg = getErrorMessage(error, "Error update contacts");
              showToast({
                title: errMsg,
                type: "error",
              });
              return;
            }
          }
        }
      }
      pull();
      showToast({
        type: "success",
        title: "Success update contact",
      });
    } catch (error) {
      const errMsg = getErrorMessage(error, "Error update contact");
      showToast({
        title: errMsg,
        type: "error",
      });
    }
  });

  if (contactsLoading || !contact) {
    return null; // Or return a loading component / Will redirect via useEffect if !contact
  }

  return (
    <FormProvider {...contactFormMethods}>
      <UpdateContactScreen
        onSubmit={onSubmit}
        updateContactLoading={updateContactLoading}
        showTooltip={isEdit}
      />
    </FormProvider>
  );
};
